import { useEffect, useRef } from 'react';

interface ScannerOptions {
  /** Called with the decoded buffer on Enter (or on idle when flushOnIdleMs is set). */
  onScan: (code: string) => void;
  /** Max ms between keystrokes to still count as one scan (default 50). */
  interKeyGapMs?: number;
  /** Minimum buffer length to treat as a scan vs a stray Enter (default 3). */
  minLength?: number;
  /**
   * When set, also flush the buffer this many ms after the LAST keystroke of a
   * machine-fast burst — i.e. auto-fire even for scanners that send NO trailing
   * Enter/CR. Safe: human typing (inter-key gaps > interKeyGapMs) never
   * accretes the buffer past ~1 char, so the idle flush only fires for a real
   * scan. Off by default (Enter-only).
   */
  flushOnIdleMs?: number;
  enabled?: boolean;
}

/**
 * Keyboard-wedge barcode scanner listener.
 *
 * Scanners type a burst of characters then (usually) send Enter. We buffer
 * keystrokes and flush on Enter when the inter-key cadence looks machine-fast.
 * With `flushOnIdleMs` we also flush after the burst goes idle, so a scanner
 * with no Enter suffix still auto-fires. Keystrokes into a focused input are
 * ignored UNLESS the input carries a `data-scanner-target` attribute.
 */
export function useBarcodeScanner({
  onScan,
  interKeyGapMs = 50,
  minLength = 3,
  flushOnIdleMs,
  enabled = true,
}: ScannerOptions): void {
  const buffer = useRef('');
  const lastTime = useRef(0);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;

    const clearIdle = () => {
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
    };
    const flush = () => {
      const code = buffer.current.trim();
      buffer.current = '';
      clearIdle();
      if (code.length >= minLength) onScanRef.current(code);
    };

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      const isScannerTarget = target?.hasAttribute?.('data-scanner-target') ?? false;
      if (isEditable && !isScannerTarget) return;

      const now = Date.now();
      const gap = now - lastTime.current;
      lastTime.current = now;

      if (e.key === 'Enter') {
        flush();
        return;
      }

      // A human-typed character resets the buffer; a machine burst accretes it.
      if (gap > interKeyGapMs && buffer.current.length > 0) {
        buffer.current = '';
      }
      if (e.key.length === 1) {
        buffer.current += e.key;
      }

      // No Enter suffix? Auto-flush once the burst goes quiet.
      if (flushOnIdleMs) {
        clearIdle();
        idleTimer.current = setTimeout(flush, flushOnIdleMs);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearIdle();
    };
  }, [enabled, interKeyGapMs, minLength, flushOnIdleMs]);
}
